import type { DexieMigrationTable } from '@infra/persistence/Migrations/dexie_to_sqlite';
import { TenantContext } from '@infra/tenancy/TenantContext';
import type { IRepository } from './IRepository';
import { SqliteEntityBridge } from './SqliteEntityBridge';

/** Repository générique sur SQLite chiffré (via IPC). */
export class SqliteJsonRepository<
  T extends { id: string; tenant_id?: string },
> implements IRepository<T> {
  constructor(
    private readonly tableName: DexieMigrationTable,
    private readonly tenantScoped: boolean = true,
  ) {}

  private matchCriteria(entity: T, criteria: Partial<T>): boolean {
    return Object.entries(criteria).every(
      ([key, value]) => (entity as Record<string, unknown>)[key] === value,
    );
  }

  async findById(id: string): Promise<T | null> {
    return SqliteEntityBridge.get<T>(this.tableName, id);
  }

  private activeTenant(): string | undefined {
    if (!this.tenantScoped) return undefined;
    return TenantContext.getInstance().tryGetTenant()?.id;
  }

  async findAll(): Promise<T[]> {
    return SqliteEntityBridge.list<T>(this.tableName, this.activeTenant());
  }

  async findBy(criteria: Partial<T>): Promise<T[]> {
    const tenantId =
      this.tenantScoped && typeof criteria.tenant_id === 'string' ? criteria.tenant_id : undefined;
    const rows = await SqliteEntityBridge.list<T>(this.tableName, tenantId);
    return rows.filter((entity) => this.matchCriteria(entity, criteria));
  }

  async save(entity: T): Promise<T> {
    return SqliteEntityBridge.put(this.tableName, entity);
  }

  async saveMany(entities: T[]): Promise<T[]> {
    for (const entity of entities) {
      await SqliteEntityBridge.put(this.tableName, entity);
    }
    return entities;
  }

  async delete(id: string): Promise<void> {
    await SqliteEntityBridge.delete(this.tableName, id);
  }

  async count(criteria?: Partial<T>): Promise<number> {
    if (!criteria) {
      return SqliteEntityBridge.count(this.tableName, this.activeTenant());
    }
    return (await this.findBy(criteria)).length;
  }

  async get(id: string): Promise<T | undefined> {
    const row = await this.findById(id);
    return row ?? undefined;
  }

  async list(): Promise<T[]> {
    return this.findAll();
  }

  async add(entity: T): Promise<void> {
    await this.save(entity);
  }

  async update(entity: T): Promise<void> {
    await this.save(entity);
  }
}
